import { z, type ZodType } from '@customerio/hydra-validation';
import type {
  FacebookLeadAdsCredential as UIAPIFacebookLeadAdsCredential,
  FacebookLeadAdsForm as UIAPIFacebookLeadAdsForm,
} from '@customerio/hydra-ui-api';
import { defineEntity } from '../shared/define-entity';

/**
 * Facebook Lead Ads credential + forms (workspace-scoped). The integrations
 * directory reads credential presence (Facebook Lead Ads "enabled") and the
 * count of connected forms (contributes to the Forms card's total).
 */
export type FacebookLeadAdsCredential = UIAPIFacebookLeadAdsCredential;
export type FacebookLeadAdsForm = UIAPIFacebookLeadAdsForm;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const FacebookLeadAdsCredentialFromApi = z.looseObject({
  page_id: z.string(),
}) as unknown as ZodType<FacebookLeadAdsCredential, unknown>;

// oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
const FacebookLeadAdsFormFromApi = z.looseObject({
  id: z.string(),
}) as unknown as ZodType<FacebookLeadAdsForm, unknown>;

/** @public */
export const facebookLeadAdsForm = defineEntity<FacebookLeadAdsForm, FacebookLeadAdsForm, void, undefined, 'workspace'>(
  {
    name: 'facebook_lead_ads_form',
    scope: 'workspace',
    pk: (f) => f.id,
    schema: { read: FacebookLeadAdsFormFromApi },
  },
);

export const facebookLeadAdsFormKeys = facebookLeadAdsForm.keys;

/** Credential is a per-workspace singleton — keyed under the form entity's namespace. */
export const facebookLeadAdsCredentialKey = (workspaceId: number) =>
  [...facebookLeadAdsForm.keys.all(workspaceId), 'credential'] as const;

export function parseFacebookLeadAdsCredential(raw: UIAPIFacebookLeadAdsCredential): FacebookLeadAdsCredential {
  // oxlint-disable-next-line typescript/consistent-type-assertions -- pre-existing assertion; replace with narrowing when touched
  return FacebookLeadAdsCredentialFromApi.parse(raw) as FacebookLeadAdsCredential;
}

export function parseFacebookLeadAdsForm(raw: UIAPIFacebookLeadAdsForm): FacebookLeadAdsForm {
  return facebookLeadAdsForm.parse(raw);
}

/** `facebook-lead-ads-form.isConnected` — a form that is actively connected. */
export function isConnectedFacebookForm(form: Pick<FacebookLeadAdsForm, 'status'>): boolean {
  return form.status === 'connected';
}
